import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import update from 'immutability-helper';
import { useHistory } from 'react-router';
import classNames from 'classnames';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Types
import { GoalItem } from 'Shared/constants/types';

// Actions
import {
  moveGoal,
  moveGoalWithoutRequest,
  markGoalIncomplete,
  markGoalComplete,
  changeStatus,
} from 'Shared/actions/Goals.actions';

// Components
import GoalCard from '../GoalCard/GoalCard';

// Styles
import './GoalsCardContainer.scss';

type Props = {
  goals: GoalItem[];
  draggable?: boolean;
  useFlexLayout?: boolean;
};

function indicesToPositions(items) {
  return items.map((item, index) => ({ ...item, position: index + 1 }));
}

const GoalsCardContainer = ({ goals, draggable = false, useFlexLayout = false }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const handleMarkComplete = useCallback(
    ({ goalSlug, closeActionItems, resolutionNote }) => {
      dispatch(markGoalComplete({ closeActionItems, goalSlug, resolutionNote }));
    },
    [dispatch]
  );

  const handleMarkIncomplete = useCallback(
    slug => {
      dispatch(markGoalIncomplete(slug));
    },
    [dispatch]
  );

  const handleChangeStatus = useCallback(
    (slug, status) => {
      dispatch(changeStatus(slug, status));
    },
    [dispatch]
  );

  const redirectToGoalDetailPage = useCallback(
    slug => {
      history.push({
        pathname: PageUrlsUtils.makeGoalDetailPageUrl(slug),
        state: { modal: true },
      });
    },
    [history]
  );

  const handleMoveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const dragCard = goals[dragIndex];
      const newGoals = indicesToPositions(
        update(goals, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard],
          ],
        })
      );
      dispatch(moveGoalWithoutRequest(newGoals));
    },
    [dispatch, goals]
  );

  const handleDrop = useCallback(() => {
    dispatch(moveGoal(goals));
  }, [dispatch, goals]);

  const baseClassName = 'GoalsCardContainer';
  const containerClasses = classNames(baseClassName, useFlexLayout && `${baseClassName}--flex`);
  const cardClassName = classNames(useFlexLayout ? `${baseClassName}__goal-flex` : `${baseClassName}__goal`);

  return (
    <div className={containerClasses}>
      {goals.map((goal, index) => (
        <GoalCard
          key={goal.slug}
          goal={goal}
          classnames={cardClassName}
          moveCard={handleMoveCard}
          onDrop={handleDrop}
          index={index}
          draggable={draggable}
          onMarkComplete={handleMarkComplete}
          handleMarkIncomplete={handleMarkIncomplete}
          handleChangeStatus={handleChangeStatus}
          redirectToGoalDetailPage={redirectToGoalDetailPage}
        />
      ))}
    </div>
  );
};
export default GoalsCardContainer;
